import { Link } from '@tanstack/react-router';
import type { Role } from '@elutide/shared';
import type { NavSection } from './nav';
import { Button, Pill } from './Primitives';
import { Icon } from './icons';

export interface SidebarProps {
  sections: readonly NavSection[];
  collapsed: boolean;
  onToggle: () => void;
  role: Role | null;
  connected: boolean;
  onLock?: () => void;
  footer?: React.ReactNode;
}

/**
 * Wordmark, the sections the role can reach with their count badges, then the role chip, the
 * data connection dot and Lock. Collapses to a 56px icon rail and remembers the state.
 */
export function Sidebar({
  sections,
  collapsed,
  onToggle,
  role,
  connected,
  onLock,
  footer,
}: SidebarProps) {
  return (
    <nav
      className={collapsed ? 'o-sidebar o-sidebar--collapsed' : 'o-sidebar'}
      aria-label="Sections"
    >
      <div className="o-sidebar__brand">
        <span className="o-sidebar__mark" aria-hidden="true" />
        {collapsed ? null : (
          <span className="o-sidebar__word">
            Elutide<em>Ops</em>
          </span>
        )}
      </div>

      <div className="o-sidebar__nav">
        {sections.map((section) => (
          <Link
            key={section.id}
            to={section.href}
            className="o-sidebar__link"
            activeOptions={{ exact: section.href === '/' }}
            title={collapsed ? section.label : undefined}
          >
            <span className="o-sidebar__icon" aria-hidden="true">
              <Icon name={section.icon} />
            </span>
            {collapsed ? (
              <span className="o-visually-hidden">{section.label}</span>
            ) : (
              <span className="o-sidebar__label">{section.label}</span>
            )}
            {section.badge !== undefined && section.badge > 0 ? (
              <span
                className={
                  section.id === 'stock'
                    ? 'o-sidebar__badge o-sidebar__badge--crit'
                    : 'o-sidebar__badge'
                }
                aria-label={`${section.badge} in ${section.label}`}
              >
                {section.badge}
              </span>
            ) : null}
          </Link>
        ))}
      </div>

      <div className="o-sidebar__foot">
        {footer}
        {collapsed ? null : (
          <div className="o-row">
            <Pill tone={role === 'owner' ? 'copper' : 'neutral'}>{role ?? 'signed out'}</Pill>
            <Pill tone={connected ? 'ok' : 'crit'}>{connected ? 'live' : 'no data'}</Pill>
          </div>
        )}
        <div className="o-row">
          <Button size="sm" variant="ghost" onClick={onToggle}>
            {collapsed ? 'Expand' : 'Collapse'}
          </Button>
          {onLock ? (
            <Button size="sm" variant="ghost" onClick={onLock}>
              Lock
            </Button>
          ) : null}
        </div>
      </div>
    </nav>
  );
}
